import { useCallback, useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  allUnselectedState,
  getDesignFiltersParams,
  getDesignFiltersState,
  type useDesignFiltersParams,
} from './';

export interface DesignFilterItems<T> {
  label: string;
  selected: boolean;
  value: T;
  count?: number;
}

export interface DesignFilter<T> {
  items: DesignFilterItems<T>[];
  open: boolean;
}

export interface DesignFilters {
  tags: DesignFilter<string>;
  published: DesignFilter<boolean>;
}

export interface DesignPanel {
  open: boolean;
}

export interface DesignFiltersState {
  filters: DesignFilters;
  panel: DesignPanel;
}

export type DesignFiltersAction =
  | { type: 'CLEAR_FILTERS' }
  | { type: 'REINITIALIZE'; params: string }
  | ({ type: 'SET_FILTER'; keepPage?: boolean; open?: boolean } & {
      [K in keyof DesignFilters]: { field: K; items: DesignFilters[K]['items'] };
    }[keyof DesignFilters])
  | { type: 'TOGGLE_FILTER'; field: keyof DesignFilters }
  | { type: 'TOGGLE_PANEL' };

export const useDesignFiltersReducer = (
  params: ReturnType<typeof useDesignFiltersParams>,
  designTags: readonly string[] | undefined,
) => {
  const [_, setSearchParams] = useSearchParams();

  const reducer = useCallback(
    (state: DesignFiltersState, action: DesignFiltersAction): DesignFiltersState => {
      switch (action.type) {
        case 'CLEAR_FILTERS': {
          setSearchParams((params) => {
            Object.keys(state.filters).forEach((x) => params.delete(x));
            params.set('page', '1');
            return params;
          });

          return allUnselectedState;
        }

        case 'REINITIALIZE': {
          const urlSearchParams = new URLSearchParams(action.params);
          const params = getDesignFiltersParams(urlSearchParams);
          setSearchParams(() => urlSearchParams);
          return getDesignFiltersState(params);
        }

        case 'SET_FILTER': {
          const {
            field,
            items,
          }: { field: keyof DesignFilters; items: DesignFilterItems<boolean | string>[] } = action;
          const { keepPage = false } = action;
          const selected = items.filter((x) => x.selected);
          setSearchParams((params) => {
            selected.length > 0
              ? params.set(field, JSON.stringify(selected.map((x) => x.value)))
              : params.delete(field);
            if (!keepPage) params.set('page', '1'); // reset pagination when filters change
            return params;
          });
          return {
            ...state,
            filters: {
              ...state.filters,
              [field]: {
                ...state.filters[field],
                ...(action.open != null && { open: action.open }),
                items,
              },
            },
          };
        }

        case 'TOGGLE_FILTER': {
          return {
            ...state,
            filters: {
              ...state.filters,
              [action.field]: {
                ...state.filters[action.field],
                open: !state.filters[action.field].open,
              },
            },
          };
        }

        case 'TOGGLE_PANEL': {
          return {
            ...state,
            panel: { ...state.panel, open: !state.panel.open },
          };
        }

        default: {
          return state;
        }
      }
    },
    [setSearchParams],
  );

  const [state, dispatch] = useReducer(reducer, getDesignFiltersState(params));

  // Update tags when designTags from GraphQL changes
  useEffect(() => {
    if (!designTags) return;
    if (designTags.join() === state.filters.tags.items.map((x) => x.value).join()) return;
    dispatch({
      type: 'SET_FILTER',
      field: 'tags',
      items: designTags.map((x) => ({
        label: x,
        selected: params.tags.includes(x),
        value: x,
      })),
      keepPage: true,
      open: params.tags.length > 0,
    });
  }, [designTags, params.tags, state.filters.tags.items]);

  return [state, dispatch] as const;
};
